import React, { useMemo, useCallback, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faTimes } from "@fortawesome/free-solid-svg-icons";
import ThemeableAgGrid from "../../../common/components/agGrid/ThemeableAgGrid";
import { useTranslation } from "react-i18next";

const UserAccountSubscriptionsTable = ({
  userAccountSubscriptions,
  onRowClick,
  onSort,
}) => {
  const { t } = useTranslation(["auth"]);
  const [pageSize, setPageSize] = useState(20);

  // Memoize column definitions
  const columnDefs = useMemo(
    () => [
      {
        field: "login",
        headerName: t("UserAccount"),
        sortable: true,
        filter: true,
        flex: 1,
      },
      {
        field: "organizationSubscriptionName",
        headerName: t("SubscriptionName"),
        sortable: true,
        filter: true,
        flex: 1,
      },
      {
        field: "contributor",
        headerName: t("Contributor"),
        sortable: true,
        filter: true,
        flex: 1,
        headerClass: "ag-center-aligned-header",
        cellRenderer: (params) => {
          const isActive = params.data?.contributor;
          return (
            <div className="centered" style={{ width: "100%", height: "100%" }}>
              <FontAwesomeIcon
                icon={isActive ? faCheck : faTimes}
                className={isActive ? "text-success" : "text-danger"}
                style={{ fontSize: "16px" }}
              />
            </div>
          );
        },
      },
      {
        field: "active",
        headerName: t("Active"),
        sortable: true,
        filter: true,
        // Apply centered class for better alignment
        cellClass: "ag-center-aligned-cell",
        flex: 1,
        headerClass: "ag-center-aligned-header",
        cellRenderer: (params) => {
          const isActive = params.data?.active;
          return (
            <div className="centered" style={{ width: "100%", height: "100%" }}>
              <FontAwesomeIcon
                icon={isActive ? faCheck : faTimes}
                className={isActive ? "text-success" : "text-danger"}
                style={{ fontSize: "16px" }}
              />
            </div>
          );
        },
      },
    ],
    [t]
  );

  // Memoize default column properties
  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      resizable: true,
      filter: true,
      minWidth: 100,
    }),
    []
  );

  // Handle sort changed event
  const handleSortChanged = useCallback(
    (event) => {
      const { api } = event;
      const columnState = api.getColumnState();

      if (columnState && columnState.length > 0) {
        const sortedColumn = columnState.find((col) => col.sort);
        if (sortedColumn && onSort) {
          onSort(sortedColumn.colId, sortedColumn.sort);
        }
      }
    },
    [onSort]
  );

  // Pagination configuration
  const paginationConfig = useMemo(
    () => ({
      // Enable pagination for all tables regardless of size
      enabled: true,
      // Default page size
      pageSize: 20,
      // Available page size options for user to choose
      pageSizeOptions: [5, 10, 20, 50, 100],
    }),
    []
  );

  // Handle page size change
  const handlePageSizeChange = useCallback((newPageSize) => {
    setPageSize(newPageSize);
  }, []);

  return (
    <ThemeableAgGrid
      className="table-bold clickable"
      columnDefs={columnDefs}
      defaultColDef={defaultColDef}
      rowData={userAccountSubscriptions}
      onSortChanged={handleSortChanged}
      onRowClicked={(params) => onRowClick && onRowClick(params.data)}
      getRowId={(params) => params.data.userAccountSubscriptionId.toString()}
      animateRows={true}
      // Normal layout for pagination
      domLayout="autoHeight"
      // Enable pagination
      pagination={true}
      paginationPageSize={pageSize}
      paginationPageSizeSelector={paginationConfig.pageSizeOptions}
      onPaginationChanged={(event) => {
        const newPageSize = event.api.paginationGetPageSize();
        if (newPageSize !== pageSize) {
          handlePageSizeChange(newPageSize);
        }
      }}
      suppressPaginationPanel={false}
    />
  );
};

export default React.memo(UserAccountSubscriptionsTable);

